import styled from '@emotion/styled';
import React, { useEffect } from 'react';
import { AppConfig } from '../../app.config';
import { Dealer } from '../../gql/graphql';
import { Role } from '../../types/role';
import { modalLoaderHelper } from '../../helpers/modal/modal.helper';
import { currentDomain, deleteQueryParam } from '../../helpers/window/window.helpers';
import { useReportError } from '../../hooks/dtrum';
import { SegmentProps } from '../../state-machine/actions/segment';
import { colors, fontFamilies } from '../baseStyle/Variables';
import Header from '../header/Header';
import Message from '../message/Message';
import { Trans, useTranslation } from 'react-i18next';

const SecurityErrorWrapper = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;
  font-family: ${fontFamilies.gilroyRegular};
  justify-content: left;
  margin: 0 auto;
  padding: 1rem 1rem;

  span {
    text-decoration: underline;
    color: ${colors.koalafiBlue};
  }

  p {
    font-size: 20px;
    line-height: 24px;
  }
`;

export enum SecurityErrorType {
  Forbidden403,
  Unauthorized401,
}

type Props = SegmentProps & {
  orderId: string;
  role: Role | undefined;
  dealer: Dealer | undefined;
  error?: any;
  errorType: SecurityErrorType;
};

const CustomSecurityErrorBoundary: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  let { error, errorType } = props;
  const reportErr = useReportError();

  if (error) {
    reportErr(error);
    console.error(error);
    console.error(error?.stack);
    console.trace();
  }

  const formattedNumberMsg = () => (
    <>
      <Trans i18nKey={($) => $.componentCustomSecurityErrorBoundary.tryAgainOrReachOut} components={{ span: <span /> }} />{' '}
    </>
  );

  let headerMsg: string;
  let displayMsg: string | JSX.Element;
  let additionalMsg = '';
  // Never translated: this feeds a Segment event name, which must match across English and Spanish sessions
  let segmentMsg: string;
  let buttonText = '';

  if (errorType === SecurityErrorType.Unauthorized401) {
    if (props.role && props.role !== Role.Customer) {
      headerMsg = t(($) => $.componentCustomSecurityErrorBoundary.sessionExpired);
      displayMsg = t(($) => $.componentCustomSecurityErrorBoundary.logBackIn);
      segmentMsg = 'Session expired';
      buttonText = t(($) => $.componentCustomSecurityErrorBoundary.login);
    } else {
      headerMsg = t(($) => $.componentCustomSecurityErrorBoundary.loggedOut);
      let storeNameStr = props?.dealer?.storeName ? ` at ${props?.dealer?.storeName}` : '';
      displayMsg = `${t(($) => $.componentCustomSecurityErrorBoundary.savedProgress)}${storeNameStr}. ${t(
        ($) => $.componentCustomSecurityErrorBoundary.verifyIdentity,
      )}`;
      additionalMsg = t(($) => $.componentCustomSecurityErrorBoundary.troubleCookies);
      segmentMsg = '401 unauthorized';
      buttonText = t(($) => $.componentCustomSecurityErrorBoundary.continueApplication);
    }
  } else {
    // will be 403 if not 401
    headerMsg = t(($) => $.componentCustomSecurityErrorBoundary.somethingWentWrong);
    displayMsg = formattedNumberMsg();
    segmentMsg = '403 access forbidden';
  }

  const handleClick = () => {
    props.track('SecurityErrorBoundary - user restarted session');
    if (props.role && props.role !== Role.Customer) {
      if (props.role === Role.Company || props.role === Role.Group || props.role === Role.Store) {
        window.location.href = AppConfig.dealerPortalUrl;
        return;
      } else {
        window.location.href = AppConfig.associatePortalUrl;
        return;
      }
    }

    // add parentDomain query param if we are in a modal and query param doesn't already exist (can't successfully set queryParam after reload)
    if (window.location !== window.parent.location) {
      const parentDomainQueryParam = new URLSearchParams(document.location.search.substring(1)).get('parentDomain');
      if (!parentDomainQueryParam) {
        const url = new URL(document.location.href);
        url.searchParams.set('parentDomain', document.referrer);
        window.history.pushState({}, '', url);
      }

      // start SDK spinner (as UI spinner doesn't show in a modal)
      const sendDomain = props?.dealer?.domains || [currentDomain()]; // Using current domain if domains not loaded
      modalLoaderHelper({ startSpinner: true }, sendDomain);
    }

    // If OOBA token was expired or improperly formatted, get rid of it so we don't bother re-checking it
    deleteQueryParam('oobaToken');

    window.location.reload();
  };

  useEffect(() => {
    props.track('SecurityErrorBoundary - ' + segmentMsg, { error }); // TODO: I removed order from this
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <SecurityErrorWrapper id="SecurityErrorBoundary">
        <Header
          orderId={props.orderId}
          dealer={props.dealer}
          goBackFunc={undefined}
          currentNode={['CustomSecurityErrorBoundary']}
          track={props.track}
          page={props.page}
          identify={props.identify}
        />
        <Message
          heading={headerMsg}
          body={displayMsg}
          additionalBody={additionalMsg}
          shouldShowButton={errorType === SecurityErrorType.Unauthorized401}
          buttonText={buttonText}
          buttonClick={handleClick}
        />
      </SecurityErrorWrapper>
    </>
  );
};

export default CustomSecurityErrorBoundary;
